import { AddOutline, IconButton } from "@bcc-ui/ui-components";
import React from "react";
import { Button as AriaButton } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Chevron from "components/icons/Chevron";
import Import from "components/icons/Import";
import { subpageContext } from "components/layout/ApplicationLayout";
import style from "components/navigation/navigation-toggle-panel.scss";
import ImportReportViews from "components/reports/ImportReportView";
import LoadReportPathsModal from "components/reports/LoadReportPathsModal";
import Tooltip from "components/tooltip/Tooltip";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    title: string;
    hasChildren?: boolean;
    chevronClass: string;
}

export const NavigationTogglePanelReports = (props: Props) => {
    const { t } = useTranslation();
    const context = React.useContext(subpageContext);
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const allowedToCreateCustomViews = userSessionService.permittedToCreateCustomViews("reports", user);
    const allowedToViewCustomViews = userSessionService.permittedToViewCustomViews("reports", user);

    const [showImportModal, setShowImportModal] = React.useState(false);

    return (
        allowedToViewCustomViews && (
            <div className={style.button}>
                <div className={style.wrapIcon}>
                    <span>{props.title}</span>
                    {allowedToCreateCustomViews && (
                        <>
                            <Tooltip content={t("Common.createNewView")} placement={"top"}>
                                <IconButton
                                    type={"button"}
                                    onClick={() => {
                                        usageStatisticsService.sendEvent({
                                            category: Category.REPORT_VIEW,
                                            action: Action.ADD_REPORT_VIEW,
                                        });
                                        context?.setSubpage({
                                            content: <LoadReportPathsModal setVisible={() => true} visible={true} />,
                                            isModal: false,
                                        });
                                    }}
                                    className={style.createViewIconButton}
                                    testId={testIds.navigation.reports.customViews.createReportViewButton}
                                    noHoverStyle
                                >
                                    <AddOutline color={resolveColor("--app-navigationPanel-iconColor")} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip content={t("ImportReportView.tooltip")} placement={"top"}>
                                <IconButton
                                    onClick={(event) => {
                                        usageStatisticsService.sendEvent({
                                            category: Category.REPORT_VIEW,
                                            action: Action.IMPORT_REPORT_VIEW,
                                        });
                                        setShowImportModal(true);
                                        event.currentTarget.blur();
                                    }}
                                    testId={testIds.navigation.reports.customViews.importReportViewButton}
                                    className={style.importIconButton}
                                    noHoverStyle
                                >
                                    <Import color={"var(--app-navigationPanel-iconColor)"} />
                                </IconButton>
                            </Tooltip>
                            <ImportReportViews modalVisible={showImportModal} setModalVisible={setShowImportModal} />
                        </>
                    )}
                </div>
                {props.hasChildren && (
                    <AriaButton>
                        <Chevron color={"var(--app-navigationPanel-iconColor)"} className={props.chevronClass} />
                    </AriaButton>
                )}
            </div>
        )
    );
};
